export interface TourRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export function intersectTourRect(a: TourRect, b: TourRect): TourRect | null {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return right > left && bottom > top
    ? { left, top, right, bottom, width: right - left, height: bottom - top }
    : null;
}

export function renderedTourTarget(element: HTMLElement) {
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const rect = element.getBoundingClientRect();
  const style = getComputedStyle(element);
  return (
    rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none'
  );
}

/** Offscreen rendered targets remain eligible: the selected one is scrolled into view first. */
export function findTourTarget(page: HTMLElement, target: string, fallback?: string) {
  const candidates = Array.from(page.querySelectorAll<HTMLElement>('[data-field-tour]'));
  const find = (value: string) =>
    candidates.find(
      (element) => element.dataset.fieldTour === value && renderedTourTarget(element),
    );
  return find(target) ?? (fallback ? find(fallback) : undefined) ?? null;
}

export function visibleTourRect(element: HTMLElement, page: HTMLElement): TourRect | null {
  const viewport = window.visualViewport;
  const left = viewport?.offsetLeft ?? 0;
  const top = viewport?.offsetTop ?? 0;
  const width = viewport?.width ?? window.innerWidth;
  const height = viewport?.height ?? window.innerHeight;
  let visible = intersectTourRect(element.getBoundingClientRect(), {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
  });
  for (let parent = element.parentElement; parent && visible; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    const rect = parent.getBoundingClientRect();
    const clipX =
      /auto|scroll|hidden|clip/.test(style.overflowX || style.overflow) || parent === page;
    const clipY =
      /auto|scroll|hidden|clip/.test(style.overflowY || style.overflow) || parent === page;
    visible = intersectTourRect(visible, {
      left: clipX ? rect.left : visible.left,
      right: clipX ? rect.right : visible.right,
      top: clipY ? rect.top : visible.top,
      bottom: clipY ? rect.bottom : visible.bottom,
      width: rect.width,
      height: rect.height,
    });
    if (parent === page) break;
  }
  return visible;
}

/** Scroll only nested Field panels; never jump the surrounding document or activate the target. */
export function scrollTourTarget(element: HTMLElement, page: HTMLElement) {
  for (
    let parent = element.parentElement;
    parent && parent !== page;
    parent = parent.parentElement
  ) {
    const style = getComputedStyle(parent);
    const outer = parent.getBoundingClientRect();
    const target = element.getBoundingClientRect();
    if (/auto|scroll/.test(style.overflowY || style.overflow)) {
      if (target.top < outer.top + 12) parent.scrollTop += target.top - outer.top - 12;
      else if (target.bottom > outer.bottom - 12)
        parent.scrollTop +=
          target.height > outer.height - 24
            ? target.top - outer.top - 12
            : target.bottom - outer.bottom + 12;
    }
    if (/auto|scroll/.test(style.overflowX || style.overflow)) {
      if (target.left < outer.left + 8) parent.scrollLeft += target.left - outer.left - 8;
      else if (target.right > outer.right - 8) parent.scrollLeft += target.right - outer.right + 8;
    }
  }
}

export function sameTourContext(
  a: { section: string; actorId?: string; tab?: string; form?: string },
  b: { section: string; actorId?: string; tab?: string; form?: string },
) {
  return a.section === b.section && a.actorId === b.actorId && a.tab === b.tab && a.form === b.form;
}

export function tourMarkerPosition(rect: TourRect, viewport: { width: number; height: number }) {
  return {
    left: Math.max(8, Math.min(rect.right - 10, viewport.width - 42)),
    top: Math.max(8, Math.min(rect.top - 12, viewport.height - 38)),
  };
}
